// src/api/soportesJuridicosApi.ts

import { getAuthToken } from '../security/AuthLogin';

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;
const API_URL = `${BASE_URL}/api`;

/** Soporte Jurídico con campos de mensajería */
export interface SoporteJuridico {
  id_concurso: string;
  id_usuario: string;
  titulo: string;
  codigo_expediente: string;
  fecha_publicacion?: string;
  enlace?: string;
  entidad_compradora?: string;
  pais?: string;
  localidad?: string;
  cliente_nombre?: string;
  cliente_email?: string;
  fecha_alta_baja?: string;
  estado_concurso?: string;
  estado_soporte?: string | null;
  dueno_id?: string | null;
  dueno_nombre?: string | null;
  dueno_email?: string | null;
  importe_total?: number;
  importe_estimado?: number;
  id_soporte?: number;

  // Campos de mensajería
  leido?: number;                   // 0 o 1
  ultimo_emisor?: string | null;    // 'nadie','cliente','admin_tecnico', etc.
  ultima_fecha_msg?: string | null;
}

/** Para listar */
export interface FetchSoportesJuridicosParams {
  page?: number;
  limit?: number;
  search?: string;
  fechaInicio?: string;
  fechaFin?: string;
  importeDesde?: string;
  importeHasta?: string;
  autor?: string;
  asignado?: string;
  cerrados?: string;
  noRespondidos?: string;
}

/** Respuesta al listar */
interface FetchSoportesJuridicosResponse {
  data: SoporteJuridico[];
  total: number;
  page: number;
  limit: number;
}

/** Respuesta al detalle */
export interface SoporteJuridicoDetalleResponse {
  success: boolean;
  soporte_juridico?: SoporteJuridico;
  message?: string;
}

/**
 * Listar soportes jurídicos (GET /api/soportes-juridicos).
 */
export const fetchSoportesJuridicos = async (
  params?: FetchSoportesJuridicosParams,
  signal?: AbortSignal
): Promise<FetchSoportesJuridicosResponse> => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const queryString = new URLSearchParams();
  if (params?.page) queryString.set('page', params.page.toString());
  if (params?.limit) queryString.set('limit', params.limit.toString());
  if (params?.search) queryString.set('search', params.search);
  if (params?.fechaInicio) queryString.set('fechaInicio', params.fechaInicio);
  if (params?.fechaFin) queryString.set('fechaFin', params.fechaFin);
  if (params?.importeDesde) queryString.set('importeDesde', params.importeDesde);
  if (params?.importeHasta) queryString.set('importeHasta', params.importeHasta);
  if (params?.autor) queryString.set('autor', params.autor);
  if (params?.asignado) queryString.set("asignado", params.asignado);
  if (params?.cerrados) queryString.set('cerrados', params.cerrados);
  if (params?.noRespondidos)
    queryString.set("noRespondidos", params.noRespondidos);

  const url = `${API_URL}/soportes-juridicos?${queryString.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al obtener soportes jurídicos: ${errorText}`);
  }

  return response.json();
};

/**
 * Detalle de un soporte jurídico (GET /api/soportes-juridicos/detalle/:id_concurso/:id_usuario).
 * Devuelve { success, soporte_juridico, message }.
 */
export const fetchSoporteJuridicoDetalle = async (
  idConcurso: string,
  idUsuario: string
): Promise<SoporteJuridicoDetalleResponse> => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const url = `${API_URL}/soportes-juridicos/detalle/${idConcurso}/${idUsuario}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    throw new Error(`Error al obtener soporte jurídico: ${errorText}`);
  }

  return resp.json();
};
